import type { AppMenuItem } from '@/config/types';

type ScreenMap = {
  sm?: boolean;
  md?: boolean;
  lg?: boolean;
};

export function shouldBeginRouteSelection(
  targetPath: string | undefined,
  currentPath: string,
) {
  return Boolean(targetPath && targetPath !== currentPath);
}

export function findMenuPath(items: AppMenuItem[], pathname: string): AppMenuItem[] {
  for (const item of items) {
    if (item.path === pathname && !item.children?.length) {
      return [item];
    }

    const childPath = item.children ? findMenuPath(item.children, pathname) : [];
    if (childPath.length) {
      return [item, ...childPath];
    }
  }

  return [];
}

export function findMenuKeyPath(items: AppMenuItem[], pathname: string): string[] {
  return findMenuPath(items, pathname).map((item) => item.key);
}

export function resolveExclusiveOpenKeys(
  nextOpenKeys: string[],
  currentOpenKeys: string[],
  rootKeys: string[],
  routeSelectionPending = false,
): string[] | undefined {
  if (routeSelectionPending) {
    return undefined;
  }

  const newlyOpenedRoot = nextOpenKeys.find(
    (key) => rootKeys.includes(key) && !currentOpenKeys.includes(key),
  );
  if (newlyOpenedRoot) {
    return [newlyOpenedRoot];
  }

  const openRoot = nextOpenKeys.find((key) => rootKeys.includes(key));
  return openRoot
    ? nextOpenKeys.filter((key) => key === openRoot || !rootKeys.includes(key))
    : [];
}

export function deferResolvedOpenKeys({
  nextOpenKeys,
  getCurrentOpenKeys,
  rootKeys,
  routeSelectionPending,
  onResolved,
}: {
  nextOpenKeys: string[];
  getCurrentOpenKeys: () => string[];
  rootKeys: string[];
  routeSelectionPending: () => boolean;
  onResolved: (openKeys: string[]) => void;
}) {
  queueMicrotask(() => {
    const resolvedOpenKeys = resolveExclusiveOpenKeys(
      nextOpenKeys,
      getCurrentOpenKeys(),
      rootKeys,
      routeSelectionPending(),
    );
    if (resolvedOpenKeys) {
      onResolved(resolvedOpenKeys);
    }
  });
}

export function resolveResponsiveNavigation(screens: ScreenMap) {
  const hasSidebarBreakpoint = screens.md === true;

  return {
    isPhone: !hasSidebarBreakpoint,
    isCompact: hasSidebarBreakpoint && screens.lg !== true,
    disableMobile: hasSidebarBreakpoint,
  };
}
